'use client'

/**
 * The feedback-mode element picker: a full-viewport capture layer that turns
 * a pick into a `NormalizedFeedbackAnchor` (Issue #13577, epic #13573;
 * touch support #13640).
 *
 * ## The shape, and why it is this shape
 *
 * A single portal layer over `document.body` with `pointer-events: auto` is
 * what stops the click reaching the app underneath — without it, picking the
 * "New plan" button would also press it. The outline and the label drawn on
 * top of that layer are `pointer-events: none`, so they never become their
 * own hit targets; the CONTROL BAR is the one deliberate exception, and it
 * carries `data-feedback-mode-exempt` so the picker cannot anchor to it.
 *
 * That capture layer is also the reason hit-testing needs two steps:
 * `document.elementFromPoint` would return the layer itself, so the layer
 * disables its own pointer events for the duration of the single call and
 * restores them immediately. The header's "Send Feedback" button, the
 * user-menu row and the mode banner all opt out the same way, via
 * `data-feedback-mode-exempt` — picking the control you are standing on
 * produces an anchor nobody wants, and picking the banner anchors feedback to
 * the notice explaining what feedback mode is.
 *
 * ## The z-index is a contract, not a preference
 *
 * Two bands bracket this layer (#16841). Every ordinary `@/components/ui/modal`
 * renders at `z-modal`, and every sai-ui menu at `z-menu`; `FeedbackModal`
 * renders at `z-feedback-modal` (`layer="feedback"`). This layer sits at
 * `z-feedback-picker`, between them: ABOVE an open dialog or menu, so feedback
 * can be given about an element inside it (before #16841 it was under every
 * dialog's backdrop, and picking inside a dialog was impossible), and BELOW
 * the feedback form. The names are sai-ui's layer scale plus Cadence's
 * feedback extension tokens since #16990 (they were 500 / 540 / 600). It additionally UNMOUNTS the moment an anchor is
 * pending, so the form that opens to receive that anchor is never behind the
 * thing that produced it. Full ladder: `LAYER_CLASSES` in `ui/modal.tsx`.
 *
 * ## Listeners on the node, not React props
 *
 * `mousemove` / `click` are registered on the layer through a ref. A `<div>`
 * carrying an `onClick` prop is an a11y lint violation for good reason — it
 * promises a keyboard path that a pointer-position-driven picker cannot
 * offer. The keyboard path for this feature is elsewhere and real: `Shift+F`
 * enters (#13715 — `Shift+M` is deleted), the control bar takes focus on
 * open, `Enter` writes the feedback, the banner's `Done` and `Escape` leave,
 * and none of it needs the layer itself to be focusable.
 *
 * The layer's own `click` listener fires BEFORE React's delegated handlers
 * (React attaches those at the portal container, `document.body`, which is an
 * ancestor of this layer). So it must ignore any click whose target is inside
 * the exempt control bar, or pressing "Write Feedback" would first be read as
 * a tap on whatever sits under the toolbar.
 *
 * ## Pointer capability, not viewport width (#13640)
 *
 * v1 suppressed the whole mode below `md` and called that "desktop only". It
 * was not: `useIsMobile` is `window.innerWidth < breakpoint`, and an iPad in
 * landscape is 1024 CSS px — above the gate, with no hover to preview with,
 * so a tap picked whatever was under it sight unseen. Meanwhile a half-width
 * desktop window was excluded for no reason. The gate is now
 * `usePointerCapability()`, which asks `(hover: hover) and (pointer: fine)`
 * and re-answers when a trackpad is attached mid-session.
 *
 * Two interactions follow from that one question:
 *
 *  - FINE (mouse, trackpad, hovering pencil) — hover previews, one click
 *    commits. Unchanged from #13577.
 *  - COARSE (a bare finger) — the first tap PREVIEWS and commits nothing; a
 *    second tap on the same element, or the Confirm control, commits; a tap
 *    on a different element re-previews, so a mis-tap costs nothing. The
 *    v1 comment said no touch affordance exists that does not first steal the
 *    tap. The tap is already stolen by the capture layer: that is the
 *    mechanism this is built on, not an obstacle to it.
 *
 * The width floor that used to hold `FEEDBACK_MODE_MIN_VIEWPORT` is gone, and
 * the failure it existed to prevent — a `fixed inset-0` interceptor with no
 * visible way out — is closed at the source instead.
 *
 * ## The bar is COARSE-ONLY now (#13788)
 *
 * `Write Feedback` left this bar for `feedback-mode-banner.tsx`, and with it
 * the focus-on-open effect that made `Shift+F` → `Enter` work. On a fine
 * pointer this bar therefore held nothing, so it does not render there at all:
 * one amber chrome per mode instead of two.
 *
 * What survives is `Parent` / `Child` on the two-step path, and they survive
 * because they cannot move. They walk a selection the viewer has just tapped,
 * so they have to sit near the thumb and near the highlight; a banner at the
 * top of the document would cost a round trip up the page per rung.
 *
 * The picker also stands down — without leaving the mode — while a PIN
 * THREAD is open. That is what makes clicking a pin while armed work: the
 * reply textarea gets the click instead of this capture layer.
 *
 * ## Where the exit went (#13715), and why that is still closed
 *
 * `Done` is NO LONGER IN THIS BAR. It lives in `feedback-mode-banner.tsx`,
 * which `src/app/layout.tsx` mounts as a sibling of this component inside the
 * same provider — so it renders on every route, including headerless ones,
 * exactly as this bar did. The obligation #13640 took on is transferred
 * intact, not dropped; `tests/unit/components/feedback/feedback-mode-banner`
 * pins it there, and `check:source-rules` (`root-layout-feedback-mode`)
 * pins the mount.
 *
 * What that bought is the layout defect the old comment below recorded and
 * could not fix: at 320px FOUR controls laid out to 383px and wrapped. The
 * split is along what the controls ARE — the banner carries mode state and the
 * exit, this bar carries the repeated actions, at the bottom where a thumb
 * reaches and near where the viewer just tapped.
 *
 * `flex-wrap` survives the split rather than being removed as no-longer-
 * needed. Three controls at 320px is comfortable on the fine-pointer path
 * (where only the CTA renders) and tight on the coarse one, and a bar that
 * wraps to two rows is recoverable while a bar that clips its controls is not.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePathname } from 'next/navigation'
import { useFeedbackModeEscape } from '@/hooks/use-feedback-mode-escape'
import { usePickerYieldsToNewModal } from '@/hooks/use-picker-yields-to-new-modal'
import { ChevronDown, ChevronUp } from '@/components/icons'
import {
  usePointerCapability,
  type PointerCapability,
} from '@/hooks/use-pointer-capability'
import { trackFeedbackModeEvent } from '@/app/actions/feedback-mode-analytics'
import {
  buildFeedbackAnchor,
  type AnchorRectLike,
} from '@/lib/feedback/anchor-payload'
import { computeSelectorPath } from '@/lib/feedback/selector-path'
import { cn } from '@/lib/utils'
import { useFeedbackMode } from './feedback-mode-context'
import { FEEDBACK_MODE_EXEMPT_ATTRIBUTE } from './feedback-mode-attributes'

/** The label shown for a target this version cannot anchor into. */
export const UNSUPPORTED_ELEMENT_LABEL = 'unsupported element'

/*
 * The overlay's testids are written as LITERALS at their JSX sites below, not
 * through constants.
 *
 * `tests/scripts/golden-path-e2e-wiring.test.ts` resolves every id a tagged
 * e2e spec asserts via `getByTestId('<literal>')` against one of five literal
 * spellings under `src/` — `data-testid="…"` being the common one. An id
 * emitted through a constant has no such spelling, so the gate reports
 * `nothing under src/ renders data-testid="…"` and reddens
 * `Repository Checks (run)` and, through it, the required `Drift Guards` and
 * `Formatting` rollups. #13640's spec is the first tagged spec to assert these
 * three, which is why the constants were fine until now and are not any more.
 */

/*
 * Both sentences name the chrome that now CARRIES the controls (#13788).
 * `Write Feedback` left this bar for the banner, so an announcement still
 * telling a non-visual user to "choose Write Feedback" below would send them
 * to a control that is not there — the same failure as an unlabelled button,
 * reached through the one channel they cannot check for themselves.
 */
const FINE_ANNOUNCEMENT =
  'Feedback mode on. Click an element to comment, or choose Write feedback ' +
  'in the banner above to send feedback without one. Esc to exit.'

const COARSE_ANNOUNCEMENT =
  'Feedback mode on. Tap an element to preview it, then tap it again to ' +
  'comment on it. Use the parent and child controls to change the ' +
  'selection, or the exit control in the banner above to leave.'

/** How far above the outline the label chip sits, in pixels. */
const LABEL_OFFSET = 22

/**
 * Every control in the bar is a real button at the repo's 44x44 floor: this
 * chrome exists for fingers, and a 32px icon button is the thing it is fixing.
 */
/**
 * `border-feedback-mode-foreground` is a WCAG 1.4.11 fix, not a flourish
 * (#13715 review).
 *
 * Once the bar took the banner's amber fill so the two read as one surface,
 * every control's FILL fell below the 3:1 non-text contrast floor against it:
 * `bg-action-primary` measures 2.41:1 on `amber-500` and 1.15:1 on the
 * dark `amber-600`, and `bg-surface-panel` 1.96:1. Label contrast is fine
 * in every case — it is the control's BOUNDARY that stops being findable, so
 * axe's `color-contrast` rule (which automates 1.4.3 text contrast only, not
 * 1.4.11) cannot see this and the golden-path spec passed over it.
 *
 * The banner's own foreground token is the one color that clears 3:1 against
 * the bar in both themes (8.31:1 on `amber-500`, 5.60:1 on `amber-600`), so
 * the boundary is drawn in the token the surface already owns rather than a
 * new one.
 */
const CONTROL_CLASS =
  'inline-flex items-center justify-center gap-1 pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px] ' +
  'rounded-md border border-feedback-mode-foreground px-3 py-1.5 text-sm ' +
  'font-medium transition-colors ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

/**
 * The step controls' text labels, hidden below 400px (#13715).
 *
 * MEASURED, not guessed, in Chromium at `hasTouch: true` on this repo's dev
 * server. At 320px the bar gets 288px (a 16px gutter each side) minus 16px of
 * its own padding = 272px of content. The three controls with full labels are
 * 145 + 87 + 78 plus two 8px gaps = 326px, so it wrapped to two rows even
 * after `Done` moved to the banner — the CTA is ~50px wider than the `Confirm`
 * it replaced, which ate most of what `Done` gave back.
 *
 * Dropping these two labels takes `Parent` and `Child` to their 44px floor:
 * 145 + 44 + 44 + 16 = 249px, inside 272px with room to spare. 400px rather
 * than `sm` (640px), because at 375px — the common phone — the full labels
 * need exactly the 326px available, and an exact fit is a wrap waiting for a
 * font to load differently.
 *
 * WCAG 2.5.3 (Label in Name) is not violated by this: the rule compares the
 * accessible name against VISIBLE text, and below the breakpoint there is
 * none. `aria-label` still starts with "Parent" / "Child", so a voice-control
 * user can still say "click Parent". The CTA's label is never hidden — it is
 * the one control a viewer has to be able to read.
 */
const STEP_LABEL_CLASS = 'hidden min-[400px]:inline'

const SECONDARY_CONTROL_CLASS =
  'bg-surface-panel text-text-primary hover:bg-fill-10'

/**
 * One previewed element, plus the rungs above it the step controls walk.
 *
 * `chain[0]` is the element the pointer actually landed on and each entry
 * after it is its next pickable ancestor, so "parent" is `index + 1` and
 * "child" is `index - 1`. Keeping the chain (rather than re-deriving a parent
 * on each press) is what makes stepping REVERSIBLE: `parentElement` can walk
 * up, but nothing can walk back down to the element a finger originally hit.
 */
interface SelectionState {
  element: Element
  chain: Element[]
  index: number
  rect: AnchorRectLike
  label: string
  supported: boolean
}

function toRectLike(element: Element): AnchorRectLike {
  const rect = element.getBoundingClientRect()
  return {
    left: rect.left,
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
  }
}

/**
 * The element under the pointer, ignoring the capture layer and anything the
 * header opted out.
 *
 * `<html>` and `<body>` are excluded because an anchor on either says "the
 * page", which the route already says.
 */
function elementUnderPointer(
  layer: HTMLElement,
  clientX: number,
  clientY: number,
): Element | null {
  const previous = layer.style.pointerEvents
  layer.style.pointerEvents = 'none'
  const found = document.elementFromPoint(clientX, clientY)
  layer.style.pointerEvents = previous
  if (!found) return null
  if (layer.contains(found)) return null
  if (found.closest(`[${FEEDBACK_MODE_EXEMPT_ATTRIBUTE}]`)) return null
  if (found === document.documentElement || found === document.body) return null
  return found
}

/**
 * An `<iframe>` belongs to another document, so neither the selector path nor
 * the rect means anything on the other side of it.
 */
function isSupportedTarget(element: Element): boolean {
  return element.localName !== 'iframe'
}

/**
 * The element, then every pickable ancestor up to (but not including) the
 * `<body>`.
 *
 * Stops at the same three exclusions `elementUnderPointer` applies, because a
 * step control that can reach what a tap cannot would let a viewer anchor to
 * the exempt chrome or to the page itself by pressing "parent" enough times.
 */
/**
 * Was this click produced by a finger or a stylus rather than a mouse?
 *
 * `click` is a `PointerEvent` in every browser that has `PointerEvent` at all,
 * so `pointerType` is the honest per-event answer where the session-level
 * media query can only describe the PRIMARY pointer. Verified in Chromium: a
 * `touchscreen.tap()` delivers `PointerEvent { pointerType: 'touch' }` to a
 * plain `click` listener.
 *
 * Falls back to `false` — the fine path — when the property is absent, because
 * the absent case is an old browser with no `PointerEvent`, and there the
 * session-level `isCoarse` gate is still in force and is the only signal
 * available. This helper only ever ADDS the two-step path; it never removes it.
 */
function isTouchDerived(event: MouseEvent): boolean {
  const pointerType = (event as PointerEvent).pointerType
  return pointerType === 'touch' || pointerType === 'pen'
}

function pickableChain(element: Element): Element[] {
  const chain: Element[] = [element]
  let current = element.parentElement
  while (
    current &&
    current !== document.body &&
    current !== document.documentElement &&
    !current.closest(`[${FEEDBACK_MODE_EXEMPT_ATTRIBUTE}]`)
  ) {
    chain.push(current)
    current = current.parentElement
  }
  return chain
}

function describeSelection(chain: Element[], index: number): SelectionState {
  const element = chain[index]
  const supported = isSupportedTarget(element)
  return {
    element,
    chain,
    index,
    rect: toRectLike(element),
    label: supported
      ? computeSelectorPath(element).label
      : UNSUPPORTED_ELEMENT_LABEL,
    supported,
  }
}

export function FeedbackElementPicker() {
  const {
    isEnabled,
    mode,
    isActive,
    anchor,
    plainFeedbackRequested,
    deactivate,
    beginBrowsing,
    setAnchor,
    setSelecting,
    pinThreadOpen,
  } = useFeedbackMode()
  const pathname = usePathname()
  const layerRef = useRef<HTMLDivElement | null>(null)
  const selectedRef = useRef<Element | null>(null)
  const frameRef = useRef<number | null>(null)
  const barRef = useRef<HTMLDivElement | null>(null)
  const [selection, setSelection] = useState<SelectionState | null>(null)
  const [isBrowser, setIsBrowser] = useState(false)

  // The gate. Reactive on purpose: an iPad gains and loses a trackpad
  // mid-session, and the interaction it should get changes with it.
  const capability = usePointerCapability()
  const isCoarse = capability === 'coarse'

  // HYBRID DEVICES. The media query describes the session's PRIMARY pointer,
  // and a touchscreen laptop — or the very iPad-with-a-trackpad this feature
  // is written for — reports `fine` while its screen is still a screen you can
  // touch. Keying the interaction off the session alone therefore reinstates
  // the original defect on exactly those devices: a finger tap takes the fine
  // path and commits sight unseen, with no outline and no label. The
  // individual event knows better than the session does — a touch-derived
  // click is a `PointerEvent` with `pointerType: 'touch'` — so the commit
  // decision is made per event, and one touch latches the two-step controls on
  // for as long as the overlay stays up (a viewer who just used a finger needs
  // the Confirm control to still be there on the second tap).
  const [sawTouch, setSawTouch] = useState(false)
  const twoStep = isCoarse || sawTouch

  /**
   * Three reasons to stand down, and none of them leaves the mode.
   *
   *  - an ANCHOR is pending: the modal is opening to receive it, and the
   *    picker must not sit over the modal or keep re-anchoring behind it;
   *  - the mode is `browsing` (#13788): the explicit suspend, whose one job is
   *    letting the viewer navigate;
   *  - a PIN THREAD is open (#13788): opening a thread while armed suspends
   *    the picker for as long as the popover is up and rearms on close. No
   *    button, no decision — this is what Figma does with the comment tool
   *    active. Without it every click aimed at the reply textarea lands on
   *    this capture layer and previews the element behind the panel, which is
   *    the defect the whole issue is named for.
   */
  const visible = mode === 'placing' && anchor === null && !pinThreadOpen

  useEffect(() => setIsBrowser(true), [])

  /**
   * THE ESCAPE LADDER (#13788): modal → pin popover → exit the mode.
   *
   * It used to be `{ enabled: isActive }`, bound for the whole mode. On the
   * anchored path — where the mode deliberately stays on while the modal is up
   * — one press therefore closed the modal AND dropped the mode, so a viewer
   * who backed out of the form lost the picking session they were mid-way
   * through. Three things claim this key now and they have to take it in
   * order.
   *
   * `anchor === null` and `!plainFeedbackRequested` are the two ways the modal
   * can be up, and both yield to it (`@/components/ui/modal` binds its own
   * Escape). `!pinThreadOpen` yields to the POPOVER (Radix binds its own, and
   * returns focus to the marker). What is left is the case with neither up,
   * which is the one that means "leave the mode".
   *
   * The plain-feedback disjunct is not redundant with the anchor one: since
   * #13788 `requestPlainFeedback()` SUSPENDS the mode to `browsing` instead of
   * ending it, so that form is open with no anchor pending and the mode still
   * live — exactly the state a single `anchor === null` test misses.
   *
   * `isActive` rather than `visible`: `browsing` has no capture layer but is
   * still a mode, and it must still be exitable from the keyboard.
   *
   * The #13911 half of this contract — why the binding has to outlive the
   * event that re-enables it, and the three guards that follow from that —
   * lives with the code, in `@/hooks/use-feedback-mode-escape`.
   */
  useFeedbackModeEscape({
    isEnabled,
    canExitOnEscape:
      isActive && anchor === null && !plainFeedbackRequested && !pinThreadOpen,
    // #16841 — the capture layer is above any open ordinary modal, so while
    // it is up Escape leaves the mode before the modal stack gets the key.
    isPicking: visible,
    deactivate,
  })

  // #16841 — the flip side of drawing the capture layer above dialogs: one
  // that opens AFTER picking began would land under it, so step aside.
  usePickerYieldsToNewModal({
    isPicking: visible,
    onModalOpened: beginBrowsing,
  })

  // Once per activation, not once per route. The dependency list was
  // `[visible, pathname]`, so the event re-fired on every navigation the mode
  // survived and on every modal round-trip — which made
  // `feedback.picker_opened` a navigation counter rather than the "could
  // actually pick" number `feedback-events.ts` documents it as.
  useEffect(() => {
    if (!visible) return
    void trackFeedbackModeEvent('feedback.picker_opened', {
      route: pathname,
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `pathname` is
    // read for the payload but deliberately not a trigger; see above.
  }, [visible])

  /**
   * Publish "a target is highlighted" so the pin overlay can stand down
   * (#13756).
   *
   * The overlay is mounted elsewhere in the root layout, so the context is the
   * only channel between the two. What is published is deliberately NARROW: a
   * live preview, not merely "the picker is up". The picker is up for the
   * whole of the mode, and suppressing pins on that would mean the pins the
   * mode exists to reveal are never visible.
   *
   * ## `twoStep`, and why `selection !== null` alone was the same bug
   *
   * A first pass published `visible && selection !== null`. On a FINE pointer
   * that latches: `selection` is set by `handleMove` the first time the cursor
   * crosses any pickable element, and `handleMove` clears it only when
   * `elementUnderPointer` returns null — which happens for `<html>`, `<body>`,
   * exempt chrome and nothing else. A cursor resting anywhere on the page
   * keeps it set, so after the first mouse move the pins were suppressed for
   * the rest of the session and the Show/Hide control did nothing. That is the
   * "true for the whole of the mode" objection this block already raises
   * against `anchor === null`, reached by a different route.
   *
   * `twoStep` (coarse pointer, or a touch already seen) is the state where a
   * selection is genuinely PENDING: the viewer has tapped once, a highlight
   * and a Confirm control are up, and the second tap commits. Pins over that
   * highlight are noise, and the window ends at the commit rather than
   * persisting. On a fine pointer `handleClick` commits immediately, so there
   * is no pending window to suppress for — and none is needed: the overlay
   * root carries `data-feedback-mode-exempt`, so `elementUnderPointer` returns
   * null over a pin and the picker can never anchor feedback TO one. That is
   * the harm #13756 names ("never the thing under the cursor when someone is
   * choosing a target"), and it is prevented structurally rather than by
   * hiding the pins.
   *
   * The cleanup matters as much as the set. This component stays mounted with
   * `visible === false` when the mode is off, which the dependency below
   * already handles; the cleanup covers the other case — the whole layout
   * unmounting mid-gesture — so a latched `true` can never outlive the picker
   * and open the next session with its pins suppressed.
   */
  const hasPendingSelection = selection !== null && twoStep
  useEffect(() => {
    setSelecting(visible && hasPendingSelection)
    return () => setSelecting(false)
  }, [visible, hasPendingSelection, setSelecting])

  // Leaving the mode drops the highlight, so re-entering never opens onto a
  // stale outline around an element that has since moved or unmounted.
  useEffect(() => {
    if (visible) return
    selectedRef.current = null
    setSelection(null)
    // The hybrid latch is scoped to one visit to the overlay. A viewer who
    // used a finger once should not be held in the two-step interaction
    // forever if they go back to the trackpad on a later pick.
    setSawTouch(false)
  }, [visible])

  const select = useCallback((chain: Element[], index: number) => {
    const next = describeSelection(chain, index)
    selectedRef.current = next.element
    setSelection(next)
  }, [])

  const commit = useCallback(
    (element: Element, via: PointerCapability) => {
      if (!isSupportedTarget(element)) return
      // A separate event, not a property on the shared one: the coarse path's
      // adoption has to be readable on its own, or "does touch picking get
      // used" is answered by a number the desktop path dominates.
      if (via === 'coarse') {
        void trackFeedbackModeEvent('feedback.touch_picked', {
          route: pathname,
        }).catch(() => {})
      }
      setAnchor(
        buildFeedbackAnchor(element, {
          route: pathname,
          rect: toRectLike(element),
          viewport: { width: window.innerWidth, height: window.innerHeight },
        }),
      )
    },
    [pathname, setAnchor],
  )

  useEffect(() => {
    const layer = layerRef.current
    if (!visible || !layer) return

    // Arrow consts, not `function` declarations: TypeScript does not carry
    // the `layer` narrowing above into a hoisted declaration.
    const handleMove = (event: MouseEvent) => {
      const found = elementUnderPointer(layer, event.clientX, event.clientY)
      if (!found) {
        selectedRef.current = null
        setSelection(null)
        return
      }
      // The label costs a `querySelectorAll` verification per rung, so it is
      // recomputed only when the pointer crosses into a DIFFERENT element —
      // not on every one of the dozens of mousemove events a single gesture
      // produces.
      if (selectedRef.current === found) return
      select(pickableChain(found), 0)
    }

    const handleClick = (event: MouseEvent) => {
      // This native listener runs BEFORE React's delegated onClick for the
      // control bar (see the header). Without this guard, pressing Confirm
      // would first be read as a tap on whatever the toolbar covers.
      const target = event.target as Element | null
      if (target?.closest?.(`[${FEEDBACK_MODE_EXEMPT_ATTRIBUTE}]`)) return

      const found = elementUnderPointer(layer, event.clientX, event.clientY)
      if (!found) return

      // Per EVENT, not per session — see the `sawTouch` note above. A finger on
      // a hybrid device must get the two-step pick even though the session
      // reports a fine primary pointer.
      const viaTouch = isTouchDerived(event)
      if (viaTouch) setSawTouch(true)

      if (!isCoarse && !sawTouch && !viaTouch) {
        commit(found, 'fine')
        return
      }

      // Two-step, because a finger has no hover with which to see what it is
      // about to pick. Tapping the SAME element again is the confirm; tapping
      // a different one re-previews, so a mis-tap costs nothing.
      //
      // `contains`, not identity, and that is the part worth stating: after a
      // `Parent` step the selection is an ANCESTOR, so a tap anywhere inside
      // the highlighted region resolves to a descendant and an identity test
      // falls through to the re-preview branch — silently discarding the step
      // the viewer just took, and breaking the tap-again-to-commit affordance
      // they had just learned, with no indication why. A tap inside what is
      // visibly selected means "this one".
      const selected = selectedRef.current
      if (selected && (selected === found || selected.contains(found))) {
        commit(selected, 'coarse')
        return
      }
      select(pickableChain(found), 0)
    }

    // A bare finger emits no `mousemove` before contact, and the synthetic one
    // some touch browsers emit AFTER a tap would move the preview out from
    // under a viewer who is about to tap the same spot to confirm. A HYBRID
    // keeps the listener: its trackpad still hovers, and a hover that
    // re-previews is not a commit, so it cannot cost the viewer an anchor.
    if (!isCoarse) layer.addEventListener('mousemove', handleMove)
    layer.addEventListener('click', handleClick)
    return () => {
      layer.removeEventListener('mousemove', handleMove)
      layer.removeEventListener('click', handleClick)
    }
  }, [visible, isCoarse, sawTouch, select, commit])

  // Scroll and resize move the element without moving the pointer, so the
  // outline has to follow it. rAF-throttled: a scroll gesture fires these
  // faster than a frame can paint, and an unthrottled `getBoundingClientRect`
  // per event forces a layout each time.
  useEffect(() => {
    if (!visible) return

    function reposition() {
      if (frameRef.current !== null) return
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null
        // The ref read, and the ref WRITE, stay outside the updater: React
        // invokes an updater twice per commit under StrictMode, and a
        // side effect in there runs twice for one transition. The updater
        // below is pure.
        const element = selectedRef.current
        if (!element || !element.isConnected) {
          selectedRef.current = null
          setSelection(null)
          return
        }
        setSelection(previous =>
          previous ? describeSelection(previous.chain, previous.index) : null,
        )
      })
    }

    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [visible])

  const step = useCallback(
    (delta: number) => {
      if (!selection) return
      const index = selection.index + delta
      if (index < 0 || index >= selection.chain.length) return
      select(selection.chain, index)
    },
    [selection, select],
  )

  /**
   * THE FOCUS EFFECT IS GONE FROM HERE (#13788), and its absence is a
   * decision rather than a deletion.
   *
   * It used to do two jobs: save a keyboard viewer a traversal of the whole
   * page to reach a portal appended to `document.body`, and — since #13715 —
   * BE the implementation of "`Shift+F` then `Enter` writes feedback", by
   * landing focus on the first enabled control with the CTA rendered first.
   *
   * The CTA moved to `feedback-mode-banner.tsx`, so the effect moved with it.
   * Leaving a copy here would focus a bar that, on a fine pointer, no longer
   * renders at all — and on the coarse path would fight the banner for the
   * caret on every entry.
   */

  if (!isBrowser || !visible) return null

  const canStepUp =
    selection !== null && selection.index + 1 < selection.chain.length
  const canStepDown = selection !== null && selection.index > 0

  return createPortal(
    <div
      ref={layerRef}
      data-testid="feedback-element-picker"
      className="fixed inset-0 z-feedback-picker cursor-crosshair touch-manipulation"
    >
      {selection && (
        <>
          <div
            data-testid="feedback-element-picker-highlight"
            className={
              selection.supported
                ? 'pointer-events-none absolute rounded-sm border-2 border-action-primary'
                : 'pointer-events-none absolute rounded-sm border-2 border-stroke-light'
            }
            style={{
              left: `${selection.rect.left}px`,
              top: `${selection.rect.top}px`,
              width: `${Math.max(0, selection.rect.right - selection.rect.left)}px`,
              height: `${Math.max(0, selection.rect.bottom - selection.rect.top)}px`,
            }}
          />
          <div
            data-testid="feedback-element-picker-label"
            className="pointer-events-none absolute max-w-xs truncate rounded-md bg-surface-primary px-2 py-1 text-xs font-medium text-text-primary-reversed shadow-md"
            style={{
              left: `${selection.rect.left}px`,
              top: `${Math.max(0, selection.rect.top - LABEL_OFFSET)}px`,
            }}
          >
            {selection.label}
          </div>
        </>
      )}

      {/*
        The control bar. `data-feedback-mode-exempt` on the container covers
        every control inside it, which is what stops the picker anchoring to
        its own chrome — and what the native click listener above keys on.

        The exit control is NOT here any more (#13715) — it is in
        `feedback-mode-banner.tsx`, mounted as this component's sibling in
        `src/app/layout.tsx` so it still renders on every route including
        headerless ones. See this file's header for why that transfer keeps
        #13640's obligation intact rather than dropping it.

        The narrow-viewport layout is measured, not guessed. At 320px the four
        controls laid out to 383px, so the original non-wrapping bar centred
        with `left-1/2 -translate-x-1/2` hung 32px off BOTH edges and clipped
        `Parent` and — the part that mattered — `Done`. Dropping `Done` bought
        back ~85px and widening `Confirm` into `Write Feedback` spent ~50px of
        it, which was NOT enough on its own: re-measured at 320px the three
        full-label controls still came to 326px against 272px of content width
        and still wrapped. `STEP_LABEL_CLASS` above is what actually closes it,
        and carries the arithmetic.

        On the FINE-pointer path `Parent` and `Child` do not render at all, so
        the bar is a single 145px control at every width. `flex-wrap` stays
        regardless, because a bar that wraps is recoverable and a bar that
        clips is not.

        `inset-x-4 mx-auto w-fit` rather than `left-1/2 -translate-x-1/2`,
        because the centring technique decides the available width here. An
        absolutely-positioned box shrink-to-fits against the space left by its
        offsets, so `left-1/2` caps it at HALF the viewport — 160px at 320px —
        and the bar wrapped to a four-row tower even with `flex-wrap` on.
        Anchoring both edges gives the full width minus the gutter and lets
        `mx-auto` do the centring, so it wraps to two rows and stays on-screen.
      */}
      {/*
        THE BAR RENDERS ONLY ON THE COARSE TWO-STEP PATH (#13788).

        On a fine pointer it held exactly one control — the CTA — which made
        it a second amber chrome for one mode, duplicating the banner. That
        control is now in the banner and this bar is not rendered at all
        there, so a fine-pointer viewer sees one piece of chrome instead of
        two.

        It SURVIVES on `twoStep` because its two remaining controls cannot
        move up. `Parent` and `Child` walk a selection the viewer has just
        tapped, so they have to sit near the thumb and near the highlight;
        pushing them to a banner at the top of the document would mean a
        round trip up the page for every rung of the chain.
      */}
      {twoStep && (
        <div
          ref={barRef}
          // Addressable so the golden-path spec's axe pass can INCLUDE the bar
          // rather than only the banner (#13715 review). The bar took the
          // banner's amber fill, so it is a second surface with the same
          // contrast obligations, and a scope that stops at the banner reports
          // green over it.
          data-testid="feedback-picker-bar"
          {...{ [FEEDBACK_MODE_EXEMPT_ATTRIBUTE]: 'true' }}
          className="absolute inset-x-4 bottom-4 mx-auto flex w-fit max-w-full flex-wrap items-center justify-center gap-2 rounded-lg bg-feedback-mode-banner p-2 text-feedback-mode-foreground shadow-xl"
        >
          {/*
            Each accessible name STARTS with the control's visible text. WCAG
            2.5.3 (Label in Name) is not a style note here: a voice-control
            user says "click Parent", and a button whose accessible name is a
            sentence not containing the word Parent cannot be actuated that
            way. axe reports it as `label-content-name-mismatch`.
          */}
          <button
            type="button"
            data-testid="feedback-picker-parent"
            onClick={() => step(1)}
            disabled={!canStepUp}
            aria-label="Parent: select the enclosing element"
            className={cn(CONTROL_CLASS, SECONDARY_CONTROL_CLASS)}
          >
            <ChevronUp className="w-4 h-4" aria-hidden="true" />
            <span className={STEP_LABEL_CLASS}>Parent</span>
          </button>
          <button
            type="button"
            data-testid="feedback-picker-child"
            onClick={() => step(-1)}
            disabled={!canStepDown}
            aria-label="Child: select the element inside"
            className={cn(CONTROL_CLASS, SECONDARY_CONTROL_CLASS)}
          >
            <ChevronDown className="w-4 h-4" aria-hidden="true" />
            <span className={STEP_LABEL_CLASS}>Child</span>
          </button>
        </div>
      )}

      {/*
        The live region reports the SELECTION, not just the instructions.
        Announcing a constant string for the overlay's whole life left the
        coarse path silent to a screen reader at the one moment that matters:
        a VoiceOver user taps, something becomes selected, and nothing says
        what is about to be anchored. Since #13715 the CTA no longer changes
        its DISABLED state to signal that, so this region is the only channel
        carrying it — the button's accessible name changes too, but a name
        change on an unfocused control is not announced. The
        preview is conveyed by an outline and a `pointer-events-none` chip —
        both invisible to AT — so without this the two-step pick asks a
        non-visual user to confirm an element they were never told about.
        `Parent` / `Child` have the same problem by construction: their entire
        job is to move the selection.

        The region is rendered UNCONDITIONALLY, with its text swapped, rather
        than mounted alongside its first message. A live region generally
        announces changes to a region that already exists, so a region and its
        text inserted in the same commit is frequently not announced at all.

        #13757 gave `feedback-mode-banner.tsx` a live region of its own, for
        the same unconditional reason, and the two are siblings whenever
        feedback mode is on. Hence the testid: `getByRole('status')` now
        matches both, and a test that has to pick one by position is a test
        that breaks whenever a third region appears anywhere in the tree.
      */}
      <div
        data-testid="feedback-element-picker-status"
        role="status"
        aria-live="polite"
        className="sr-only"
      >
        {selection
          ? `Selected ${selection.label}.${
              selection.supported
                ? ' Select it again to attach feedback to it.'
                : ' This element cannot be anchored; use Write feedback in' +
                  ' the banner above to send plain feedback instead.'
            }`
          : twoStep
            ? COARSE_ANNOUNCEMENT
            : FINE_ANNOUNCEMENT}
      </div>
    </div>,
    document.body,
  )
}
